/**
 * CustomSelect - Reusable dropdown that stays within field width and renders via portal.
 * Fixes UI issue: native select dropdowns overflow field width / get clipped on mobile.
 */
import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown } from 'lucide-react';

const defaultSelectStyle = {
  width: '100%',
  background: 'rgba(255, 255, 255, 0.05)',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  borderRadius: '12px',
  padding: '12px 16px',
  color: '#ffffff',
  fontSize: '15px',
  cursor: 'pointer',
  outline: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '8px',
  textAlign: 'left',
  boxSizing: 'border-box',
  fontFamily: 'inherit'
};

const CustomSelect = ({
  value = '',
  onChange,
  options = [],
  placeholder = 'Select...',
  style = {},
  className = '',
  id,
  name,
  disabled = false
}) => {
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState(null);
  const containerRef = useRef(null);
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      const inTrigger = containerRef.current && containerRef.current.contains(e.target);
      const inDropdown = dropdownRef.current && dropdownRef.current.contains(e.target);
      if (!inTrigger && !inDropdown) {
        setOpen(false);
        setRect(null);
      }
    };
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [open]);

  const handleOpen = () => {
    if (disabled) return;
    if (!open) {
      const r = triggerRef.current?.getBoundingClientRect();
      if (r) setRect({ top: r.bottom + 4, left: r.left, width: r.width });
    }
    setOpen(!open);
  };

  const handleSelect = (opt) => {
    onChange(opt.value);
    setOpen(false);
    setRect(null);
  };

  const selectedLabel = options.find(o => String(o.value) === String(value))?.label || value || placeholder;

  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%', minWidth: 0 }}>
      <input type="hidden" name={name} value={value} readOnly />
      <button
        ref={triggerRef}
        type="button"
        id={id}
        disabled={disabled}
        onClick={handleOpen}
        className={className}
        style={{
          ...defaultSelectStyle,
          ...style,
          opacity: disabled ? 0.6 : 1,
          cursor: disabled ? 'not-allowed' : 'pointer'
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
          {selectedLabel}
        </span>
        <ChevronDown size={18} style={{ flexShrink: 0, opacity: open ? 0.7 : 1 }} />
      </button>
      {open && rect && createPortal(
        <div
          ref={dropdownRef}
          style={{
            position: 'fixed',
            top: rect.top,
            left: rect.left,
            width: rect.width,
            maxWidth: 'calc(100vw - 32px)',
            background: '#1a1f3a',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            borderRadius: '12px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
            zIndex: 9999,
            maxHeight: '240px',
            overflowY: 'auto',
            overflowX: 'hidden',
            boxSizing: 'border-box'
          }}
        >
          {options.map((opt) => (
            <button
              key={String(opt.value)}
              type="button"
              onClick={() => handleSelect(opt)}
              style={{
                width: '100%',
                padding: '10px 16px',
                border: 'none',
                background: String(value) === String(opt.value) ? 'rgba(59, 130, 246, 0.3)' : 'transparent',
                color: '#ffffff',
                fontSize: '14px',
                textAlign: 'left',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                boxSizing: 'border-box'
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
};

export default CustomSelect;
